import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog';
import { Textarea } from 'components/ui/textarea';
import { Label } from 'components/ui/label';
import { 
  ArrowLeft, Search, MapPin, Star, Shield, Award, 
  Clock, MessageCircle, Filter, X, Send, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

const SERVICES = [
  "Companionship",
  "Personal Care",
  "Medication Reminders",
  "Meal Preparation",
  "Light Housekeeping",
  "Transportation",
  "Dementia Care",
  "Overnight Care",
  "Respite Care"
];

const LANGUAGES = [
  "English", "Spanish", "Chinese", "Tagalog", "Vietnamese",
  "Korean", "French", "Hindi", "Arabic", "Portuguese",
  "Russian", "Japanese", "German", "Italian", "Polish"
];

export default function CaregiverDirectory() {
  const navigate = useNavigate();
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCity, setSearchCity] = useState('');
  const [searchZip, setSearchZip] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [maxRate, setMaxRate] = useState('');
  // const [minRating, setMinRating] = useState('');
  const [minExperience, setMinExperience] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [language, setLanguage] = useState('');

  // Request modal state
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [selectedCaregiver, setSelectedCaregiver] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [preferredSchedule, setPreferredSchedule] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);
  const [sentRequests, setSentRequests] = useState({});

  useEffect(() => {
    fetchCaregivers();
    fetchSentRequests();
  }, []);

  const fetchSentRequests = async () => {
    try {
      const response = await api.get('/caregivers/requests/sent');
      const map = {};
      const priority = { accepted: 3, pending: 2, declined: 1 };
      (response.data.requests || []).forEach(r => {
        const current = priority[map[r.caregiver_id]] || 0;
        const incoming = priority[r.status] || 0;
        if (incoming > current) {
          map[r.caregiver_id] = r.status;
        }
      });
      setSentRequests(map);
    } catch (error) {
      console.error('Failed to fetch sent requests:', error);
    }
  };

  const fetchCaregivers = async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.city) params.append('city', filters.city);
      if (filters.zip_code) params.append('zip_code', filters.zip_code);
      if (filters.services) params.append('service', filters.services);
      if (filters.max_rate) params.append('max_rate', filters.max_rate);
      // if (filters.min_rating) params.append('min_rating', filters.min_rating);
      if (filters.min_experience) params.append('min_experience', filters.min_experience);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      if (filters.language) params.append('language', filters.language);

      const response = await api.get(`/caregivers/search?${params.toString()}`);
      setCaregivers(response.data.caregivers || []);
    } catch (error) {
      console.error('Failed to fetch caregivers:', error);
      if (error.response?.status === 403) {
        toast.error('Caregiver directory is only available for families');
      } else {
        toast.error('Failed to load caregivers. Please try again.');
      }
      setCaregivers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchCaregivers({
      city: searchCity,
      zip_code: searchZip,
      services: selectedService,
      max_rate: maxRate,
      // min_rating: minRating,
      min_experience: minExperience,
      sort_by: sortBy,
      language: language
    });
  };

  const clearFilters = () => {
    setSearchCity('');
    setSearchZip('');
    setSelectedService('');
    setMaxRate('');
    setMinExperience('');
    setSortBy('');
    setLanguage('');
    fetchCaregivers();
  };

  const openRequestModal = (caregiver, e) => {
    e.stopPropagation(); // Prevent card click navigation
    setSelectedCaregiver(caregiver);
    setRequestMessage('');
    setPreferredSchedule('');
    setRequestModalOpen(true);
  };

  const handleSendRequest = async () => {
    if (!selectedCaregiver) return;
    
    setSendingRequest(true);
    try {
      await api.post(`/caregivers/${selectedCaregiver.user_id}/request`, {
        message: requestMessage,
        preferred_schedule: preferredSchedule
      });
      
      toast.success(`Request sent to ${selectedCaregiver.name}!`);
      setSentRequests(prev => ({...prev, [selectedCaregiver.user_id]: 'pending'}));
      setRequestModalOpen(false);
      setSelectedCaregiver(null);
      setRequestMessage('');
      setPreferredSchedule('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send request';
      toast.error(errorMsg);
    } finally {
      setSendingRequest(false);
    }
  };

  const getTrustBadge = (level) => {
    switch (level) {
      case 'trusted':
        return { icon: Award, label: 'Trusted Caregiver', color: 'badge-trusted text-white' };
      case 'identity_verified':
        return { icon: Shield, label: 'Identity Verified', color: 'badge-verified text-white' };
      default:
        return { icon: Shield, label: 'Basic Verified', color: 'badge-basic text-white' };
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="caregiver-directory">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Find Caregivers</h1>
                <p className="text-xs sm:text-sm text-slate-500">Connect with verified independent caregivers</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden"
            >
              <Filter className="w-4 h-4" />
              Filters
            </Button>
          </div>

          {/* Search Bar */}
          <div className={`space-y-3 ${showFilters ? '' : 'hidden lg:block'}`}>
            <div className="grid md:grid-cols-4 gap-3">
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="City"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  className="pl-10 h-11"
                  data-testid="city-input"
                />
              </div>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="ZIP Code"
                  value={searchZip}
                  onChange={(e) => setSearchZip(e.target.value)}
                  className="pl-10 h-11"
                  data-testid="zip-input"
                />
              </div>
              <Select value={selectedService} onValueChange={setSelectedService}>
                <SelectTrigger className="h-11" data-testid="service-select">
                  <SelectValue placeholder="Service Type" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map((service) => (
                    <SelectItem key={service} value={service}>{service}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button
                  onClick={handleSearch}
                  className="flex-1 h-11 bg-teal-500 hover:bg-teal-600 text-white"
                  data-testid="search-btn"
                >
                  <Search className="w-4 h-4" />
                  Search
                </Button>
                {(searchCity || searchZip || selectedService || maxRate || minExperience || sortBy || language) && (
                <Button variant="outline" onClick={clearFilters} className="h-11">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            </div>
            {/* Advanced Filters Row */}
            <div className="grid md:grid-cols-3 gap-3">
              <Input
                type="number"
                placeholder="Max $/hr"
                value={maxRate}
                onChange={(e) => setMaxRate(e.target.value)}
                className="h-11"
                data-testid="max-rate-input"
              />
              <Select value={minExperience} onValueChange={setMinExperience}>
                <SelectTrigger className="h-11" data-testid="min-experience-select">
                  <SelectValue placeholder="Experience" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1+ Years</SelectItem>
                  <SelectItem value="3">3+ Years</SelectItem>
                  <SelectItem value="5">5+ Years</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={(v) => { setSortBy(v); }}>
                <SelectTrigger className="h-11" data-testid="sort-select">
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price_low">Price: Low to High</SelectItem>
                  <SelectItem value="price_high">Price: High to Low</SelectItem>
                  <SelectItem value="experience">Most Experienced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Language Filter Row */}
            <div className="grid md:grid-cols-4 gap-3">
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="h-11" data-testid="language-select">
                  <SelectValue placeholder="Language" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((lang) => (
                    <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </header>

      {/* Banner Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1762955911431-4c44c7c3f408?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Caregiver assisting elderly person"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
        <div className="absolute bottom-4 left-4 text-white">
          <h2 className="text-2xl font-bold font-['Manrope']">Trusted Caregivers</h2>
          <p className="text-sm text-slate-200">{caregivers.length} caregivers available</p>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-full bg-slate-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-3/4" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : caregivers.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Caregivers Found</h3>
              <p className="text-slate-500 mb-4">Try adjusting your search filters or location</p>
              <Button onClick={clearFilters} variant="outline">
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {caregivers.map((caregiver) => {
              const badge = getTrustBadge(caregiver.trust_level);
              return (
                <Card
                  key={caregiver.user_id}
                  className="hover:shadow-lg transition-shadow cursor-pointer card-interactive"
                  onClick={() => navigate(`/caregivers/${caregiver.user_id}`)}
                  data-testid={`caregiver-card-${caregiver.user_id}`}
                >
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-slate-200 overflow-hidden flex-shrink-0">
                        {caregiver.picture ? (
                          <img src={caregiver.picture} alt={caregiver.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-teal-100 text-teal-700 text-xl font-bold">
                            {caregiver.name?.[0] || '?'}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">{caregiver.name}</h3>
                        <div className="flex items-center gap-1 mt-1">
                          {caregiver.city && (
                            <span className="text-sm text-slate-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {caregiver.city}, {caregiver.state}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <div className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${badge.color}`}>
                            <badge.icon className="w-3 h-3" />
                            {badge.label}
                          </div>
                        </div>
                      </div>
                    </div>

                    {caregiver.rating > 0 && (
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                          <span className="font-medium text-slate-900">{caregiver.rating.toFixed(1)}</span>
                        </div>
                        <span className="text-sm text-slate-500">({caregiver.review_count} reviews)</span>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-1 mb-3">
                      {caregiver.services?.slice(0, 3).map((service) => (
                        <Badge key={service} variant="secondary" className="text-xs">
                          {service}
                        </Badge>
                      ))}
                      {caregiver.services?.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{caregiver.services.length - 3}
                        </Badge>
                      )}
                    </div>
                    {caregiver.languages?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {caregiver.languages.slice(0, 3).map((lang) => (
                          <Badge key={lang} variant="outline" className="text-xs border-blue-200 text-blue-700 bg-blue-50">
                            {lang}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Clock className="w-4 h-4" />
                        {caregiver.total_hours || 0}h worked
                      </div>
                      {caregiver.hourly_rate && (
                        <span className="font-semibold text-slate-900">
                          ${caregiver.hourly_rate}/hr
                        </span>
                      )}
                    </div>
                    
                    {/* Request Button */}
                    {sentRequests[caregiver.user_id] === 'pending' ? (
                      <Button
                        disabled
                        className="w-full mt-4 bg-amber-100 text-amber-700 border border-amber-200 cursor-not-allowed"
                      >
                        <Clock className="w-4 h-4" />
                        Request Sent
                      </Button>
                    ) : sentRequests[caregiver.user_id] === 'accepted' ? (
                      <Button
                        disabled
                        className="w-full mt-4 bg-teal-100 text-teal-700 border border-teal-200 cursor-not-allowed"
                      >
                        <Shield className="w-4 h-4" />
                        Accepted
                      </Button>
                    ) : sentRequests[caregiver.user_id] === 'declined' ? (
                      <Button
                        onClick={(e) => openRequestModal(caregiver, e)}
                        variant="outline"
                        className="w-full mt-4 border-slate-300 text-slate-700 hover:bg-slate-50"
                        data-testid={`request-caregiver-${caregiver.user_id}`}
                      >
                        <Send className="w-4 h-4" />
                        Resend Request
                      </Button>
                    ) : (
                      <Button
                        onClick={(e) => openRequestModal(caregiver, e)}
                        className="w-full mt-4 bg-teal-500 hover:bg-teal-600 text-white"
                        data-testid={`request-caregiver-${caregiver.user_id}`}
                      >
                        <UserPlus className="w-4 h-4" />
                        Request Caregiver
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Request Modal */}
      <Dialog open={requestModalOpen} onOpenChange={setRequestModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-teal-500" />
              Request Caregiver
            </DialogTitle>
            <DialogDescription>
              Send a care request to {selectedCaregiver?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedCaregiver && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold">
                  {selectedCaregiver.name?.[0] || '?'}
                </div>
                <div>
                  <p className="font-medium text-slate-900">{selectedCaregiver.name}</p>
                  {selectedCaregiver.city && (
                    <p className="text-sm text-slate-500">{selectedCaregiver.city}, {selectedCaregiver.state}</p>
                  )}
                  {selectedCaregiver.hourly_rate && (
                    <p className="text-sm text-teal-600 font-medium">${selectedCaregiver.hourly_rate}/hr</p>
                  )}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="message">Message (Optional)</Label>
              <Textarea
                id="message"
                placeholder="Tell the caregiver about your care needs, schedule preferences, or any other details..."
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                className="min-h-[100px]"
                data-testid="request-message-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="schedule">Preferred Schedule (Optional)</Label>
              <Input
                id="schedule"
                placeholder="e.g., Weekdays 9am-5pm, Weekends mornings"
                value={preferredSchedule}
                onChange={(e) => setPreferredSchedule(e.target.value)}
                data-testid="request-schedule-input"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setRequestModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendRequest}
              disabled={sendingRequest}
              className="bg-teal-500 hover:bg-teal-600 text-white"
              data-testid="send-request-btn"
            >
              {sendingRequest ? (
                'Sending...'
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
